"use client";

import Link from 'next/link';
import { Home, ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';

const RetroGrid = ({
  angle = 65,
  cellSize = 60,
  opacity = 0.3,
  lightLineColor = "#4a4a4a",
  darkLineColor = "#2a2a2a",
}) => {
  const gridStyles = {
    "--grid-angle": `${angle}deg`,
    "--cell-size": `${cellSize}px`,
    "--opacity": opacity,
    "--light-line": lightLineColor,
    "--dark-line": darkLineColor,
  } as React.CSSProperties;

  return (
    <div
      className="pointer-events-none absolute size-full overflow-hidden [perspective:200px] opacity-[var(--opacity)]"
      style={gridStyles}
    >
      <div className="absolute inset-0 [transform:rotateX(var(--grid-angle))]">
        <div className="animate-grid [background-image:linear-gradient(to_right,var(--light-line)_1px,transparent_0),linear-gradient(to_bottom,var(--light-line)_1px,transparent_0)] [background-repeat:repeat] [background-size:var(--cell-size)_var(--cell-size)] [height:300vh] [inset:0%_0px] [margin-left:-200%] [transform-origin:100%_0_0] [width:600vw] dark:[background-image:linear-gradient(to_right,var(--dark-line)_1px,transparent_0),linear-gradient(to_bottom,var(--dark-line)_1px,transparent_0)]" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent to-90%" />
    </div>
  );
};

export default function NotFound() {
  return (
    <Layout>
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Grid */}
        <RetroGrid />
        
        {/* Purple gradient overlay */}
        <div className="absolute inset-0 bg-purple-950/20 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(0,0,0,0))]" />
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          {/* 404 Number with gradient */}
          <div className="mb-8">
            <h1 className="text-8xl md:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-600 mb-4">
              404
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto rounded-full"></div>
          </div>
          
          {/* Error Message */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Oops! Page Not Found
            </h2>
            <p className="text-xl text-gray-300 mb-2">
              Looks like this page decided to level up somewhere else.
            </p>
            <p className="text-lg text-gray-400">
              Don't worry, even the best coaches sometimes take a wrong turn.
            </p>
          </div>
          
          {/* Helpful Links */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-gray-400 mb-6">Looking for something specific?</p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link href="/features" className="text-purple-400 hover:text-purple-300 transition-colors">
                Features
              </Link>
              <Link href="/pricing" className="text-purple-400 hover:text-purple-300 transition-colors">
                Pricing
              </Link>
              <Link href="/high-value" className="text-purple-400 hover:text-purple-300 transition-colors">
                High Value Coaching
              </Link>
              <Link href="/contact" className="text-purple-400 hover:text-purple-300 transition-colors">
                Contact Us
              </Link>
              <Link href="/blog" className="text-purple-400 hover:text-purple-300 transition-colors">
                Blog
              </Link>
            </div>
          </div>
          
          {/* Fun coaching-related message */}
          <div className="mt-12 p-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg border border-purple-500/20">
            <p className="text-purple-300 text-lg font-medium">
              💪 "Every setback is a setup for a comeback!"
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Ready to get back on track? Start your coaching journey with LvlUp.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
